import { useEffect, useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import * as apiClient from 'api/apiClients';
import { useTheme } from 'context/ThemeContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';

interface Option {
    value: number;
    label: string;
    phone?: string;
    email?: string;
    address?: string;
}

interface ClientsSelectProps {
    value: number | null;
    onChange: (option: Option | null) => void;
    allowNew?: boolean;
    isClearable?: boolean;
    placeholder?: string;
    className?: string;
}

const ClientsSelect = ({
    value,
    onChange,
    allowNew = true,
    isClearable = false,
    placeholder,
    className = 'w-100',
}: ClientsSelectProps) => {
    const { t } = useTranslation(['Clients']);
    const { darkTheme } = useTheme();
    const [selectTheme, setSelectTheme] = useState(darkTheme ? selectStylesDark : selectStylesLigth);
    const [options, setOptions] = useState<Option[]>([]);

    const newClientOption: Option = {
        value: 0,
        label: t('newClient'),
        phone: t('newClientInfo'),
    };

    const fetchClients = async () => {
        const resClient = await apiClient.getAllClients();
        const clientOptions = (resClient || []).map((client: any) => ({
            value: client.ID,
            label: client.clientName,
            phone: client.clientPhone,
            email: client.clientEmail,
            address: client.clientAddress,
        }));

        if (!allowNew) {
            setOptions(clientOptions);
            return;
        }

        if (!resClient) {
            setOptions([newClientOption]);
            return;
        }

        setOptions([newClientOption, ...clientOptions]);
    };

    const filterOption = (option: any, inputValue: string) => {
        if (!inputValue) return true;
        return (
            option?.data?.label?.toLowerCase()?.includes(inputValue?.toLowerCase()) ||
            option?.data?.phone?.toLowerCase()?.includes(inputValue?.toLowerCase())
        );
    };

    useEffect(() => {
        setSelectTheme(darkTheme ? selectStylesDark : selectStylesLigth);
    }, [darkTheme]);

    useEffect(() => {
        fetchClients();
    }, [t, allowNew]);

    return (
        <Select
            options={options}
            required={!isClearable}
            styles={{
                ...selectTheme,
                control: (base: any, state: any) => ({
                    ...base,
                    ...(selectTheme.control ? selectTheme.control(base, state) : {}),
                }),
                menuPortal: (base: any) => ({
                    ...base,
                    zIndex: 2000,
                }),
            }}
            className={className}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            value={options?.find((client) => client?.value === value) || null}
            onChange={onChange}
            isMulti={false}
            isClearable={isClearable}
            placeholder={placeholder}
            filterOption={filterOption}
            isSearchable={true}
            formatOptionLabel={(e: Option) => (
                <>
                    <span className={`fw-medium ${darkTheme ? 'text-body' : 'text-muted'}`}>{e.label}</span>
                    {e.phone ? <span className="d-block text-muted fs-12">{e.phone}</span> : null}
                </>
            )}
        />
    );
};

export default ClientsSelect;
